import {
  ActionIcon,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CHANGE_TYPES,
  MODULES,
  emptyContent,
  emptyLocalizedText,
  type Change,
  type ChangeType,
  type Module,
  type ReleaseNote,
  type ReleaseNoteContent,
} from "@models/releaseNotes";
import "./ReleaseNoteForm.scss";

export interface FormValue extends ReleaseNoteContent {
  module: Module;
  version: string;
  released_at: string;
}

interface Props {
  opened: boolean;
  /** Present when editing. Module and version are then read-only. */
  existing?: ReleaseNote | null;
  onClose: () => void;
  onSubmit: (value: FormValue) => Promise<void>;
}

const emptyValue = (): FormValue => ({
  ...emptyContent(),
  module: "extraction",
  version: "",
  released_at: new Date().toISOString().slice(0, 10),
});

const fromExisting = (note: ReleaseNote): FormValue => ({
  module: note.module,
  version: note.version,
  released_at: note.released_at.slice(0, 10),
  title: { ...note.title },
  highlight: { ...note.highlight },
  changes: note.changes.map((change) => ({
    type: change.type,
    text: { ...change.text },
  })),
});

/**
 * Create and edit form.
 *
 * Module and version are disabled while editing because they are the primary key
 * on the server; changing one is not an edit but a different release. Saving is
 * deliberately permitted with fields still empty, matching the API's lenient
 * draft rules, so a note can be written over several sittings. The publish
 * action is what enforces completeness, and it reports everything missing at
 * once.
 */
export const ReleaseNoteForm = ({
  opened,
  existing,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<FormValue>(
    existing ? fromExisting(existing) : emptyValue(),
  );
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(existing);

  const update = (patch: Partial<FormValue>) =>
    setValue((current) => ({ ...current, ...patch }));

  const updateChange = (index: number, patch: Partial<Change>) =>
    setValue((current) => ({
      ...current,
      changes: current.changes.map((change, position) =>
        position === index ? { ...change, ...patch } : change,
      ),
    }));

  const addChange = () =>
    setValue((current) => ({
      ...current,
      changes: [
        ...current.changes,
        { type: "new" as ChangeType, text: emptyLocalizedText() },
      ],
    }));

  const removeChange = (index: number) =>
    setValue((current) => ({
      ...current,
      changes: current.changes.filter((_, position) => position !== index),
    }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit({
        ...value,
        released_at: new Date(`${value.released_at}T00:00:00Z`).toISOString(),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      className="release-note-form"
      opened={opened}
      onClose={onClose}
      title={
        <Title order={4}>
          {t(isEdit ? "manage.form.editTitle" : "manage.form.createTitle")}
        </Title>
      }
      size="xl"
    >
      <Stack gap="md">
        <Group grow align="flex-start">
          <Select
            label={t("manage.form.module")}
            data={MODULES.map((module) => ({
              value: module,
              label: t(`releases.module.${module}`),
            }))}
            value={value.module}
            onChange={(next) => next && update({ module: next as Module })}
            disabled={isEdit}
            allowDeselect={false}
          />
          <TextInput
            label={t("manage.form.version")}
            description={t("manage.form.versionHint")}
            placeholder="2026.07"
            value={value.version}
            onChange={(event) => update({ version: event.currentTarget.value })}
            disabled={isEdit}
          />
          <TextInput
            type="date"
            label={t("manage.form.releasedAt")}
            value={value.released_at}
            onChange={(event) =>
              update({ released_at: event.currentTarget.value })
            }
          />
        </Group>

        <Divider label={t("manage.form.titleSection")} />
        <Group grow align="flex-start">
          <TextInput
            label="EN"
            value={value.title.en}
            onChange={(event) =>
              update({ title: { ...value.title, en: event.currentTarget.value } })
            }
          />
          <TextInput
            label="DE"
            value={value.title.de}
            onChange={(event) =>
              update({ title: { ...value.title, de: event.currentTarget.value } })
            }
          />
        </Group>

        <Divider label={t("manage.form.highlightSection")} />
        <Group grow align="flex-start">
          <Textarea
            label="EN"
            description={t("manage.form.highlightHint")}
            autosize
            minRows={2}
            value={value.highlight.en}
            onChange={(event) =>
              update({
                highlight: { ...value.highlight, en: event.currentTarget.value },
              })
            }
          />
          <Textarea
            label="DE"
            autosize
            minRows={2}
            value={value.highlight.de}
            onChange={(event) =>
              update({
                highlight: { ...value.highlight, de: event.currentTarget.value },
              })
            }
          />
        </Group>

        <Divider label={t("manage.form.changesSection")} />
        {value.changes.map((change, index) => (
          <Group key={index} align="flex-start" wrap="nowrap">
            <Select
              w={140}
              data={CHANGE_TYPES.map((type) => ({
                value: type,
                label: t(`releases.changeType.${type}`),
              }))}
              value={change.type}
              onChange={(next) =>
                next && updateChange(index, { type: next as ChangeType })
              }
              allowDeselect={false}
            />
            <Textarea
              className="release-note-form__change-text"
              label="EN"
              autosize
              value={change.text.en}
              onChange={(event) =>
                updateChange(index, {
                  text: { ...change.text, en: event.currentTarget.value },
                })
              }
            />
            <Textarea
              className="release-note-form__change-text"
              label="DE"
              autosize
              value={change.text.de}
              onChange={(event) =>
                updateChange(index, {
                  text: { ...change.text, de: event.currentTarget.value },
                })
              }
            />
            <ActionIcon
              variant="subtle"
              color="red"
              mt={28}
              aria-label={t("manage.form.removeChange")}
              onClick={() => removeChange(index)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={addChange}
          className="release-note-form__add-change"
        >
          {t("manage.form.addChange")}
        </Button>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={saving} onClick={handleSubmit}>
            {t("manage.form.saveDraft")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
